import { AttributeValue } from '@/api/AttributeValueApi';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAddCreatorProductModuleMutation } from './useAddCreatorProductModuleMutation';
import { Project } from '@/api/ProjectApi';
import { presetId } from '@/components/App';
import ProductModuleApi, { ProductModule } from '@/api/ProductModuleApi';
import ProductModuleCollectionApi from '@/api/ProductModuleCollectionApi';

export function useStartCollectionMutation() {
	const dispatch = useTypedDispatch();
	const queryClient = useQueryClient();

	const { project, preset, options } = useTypedSelector((app) => app.creator);

	const addCreatorProductModuleMutation = useAddCreatorProductModuleMutation();

	const getCollectionMutation = useMutation({
		mutationFn: ProductModuleCollectionApi.mutationGetOne,
		onSuccess: (productModuleCollection) => {
			dispatch(creatorActions.selectCollection(productModuleCollection));

			if (!productModuleCollection.defaultModule)
				throw new Error('MISSING_DEFAULT_MODULE');

			const payload: Partial<CreatorProductModule> = {
				listOrder: 0,
				productModule: {
					id: productModuleCollection.defaultModule.id,
				} as ProductModule,
				selectedAttributeValues:
					productModuleCollection.defaultAttributeValues.map(
						(attributeValue) =>
							({
								id: attributeValue.id,
							}) as AttributeValue
					),
			};

			if (options.isPresetMode) payload.preset = preset;
			else payload.project = { id: project.id } as Project;

			queryClient.prefetchQuery({
				queryKey: [
					ProductModuleApi.queryKey,
					{
						sortBy: {
							key: 'listOrder',
							order: 'ASC',
						},
					},
					{
						'filter.productModuleCollection.id': productModuleCollection.id,
					},
				],
				queryFn: ProductModuleApi.getAll,
			});

			if (!options.isPresetMode && !!presetId) return;

			addCreatorProductModuleMutation.mutate(payload);
		},
	});

	return getCollectionMutation;
}
